import { enumMembers, type ApiSchema } from "../apiSchema";
import {
  readArray,
  readBoolean,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../http/jsonDecoding";

/**
 * Version labels as the server sends them (`server/src/dmw/api/routes/content/versions/
 * version_bodies.py`; ADR-0033, built provisionally, awaiting acceptance), decoded into the
 * web app's types. A label names exactly one version; its `reference` is the exact version.
 */

export type VersionBump = ApiSchema<"VersionBump">;
export type ChangeReason = ApiSchema<"ChangeReason">;
export type WorkflowEdit = ApiSchema<"WorkflowEdit">;
export type UpdateUnavailable = ApiSchema<"UpdateUnavailable">;

/** Smallest first, the order the merge form offers them. */
export const VERSION_BUMPS = enumMembers<VersionBump>({ patch: true, minor: true, major: true });
export const CHANGE_REASONS = enumMembers<ChangeReason>({
  editorial: true,
  lifecycle_changed: true,
  retired: true,
  terms_added: true,
  terms_removed: true,
  terms_renamed: true,
  term_signatures_changed: true,
  term_hierarchy_changed: true,
  statements_changed: true,
  graph_changed: true,
  shapes_added: true,
  shapes_changed: true,
  placeholders_changed: true,
  prompt_text_changed: true,
  step_kind_changed: true,
  inputs_added: true,
  required_inputs_added: true,
  inputs_removed: true,
  inputs_became_required: true,
  inputs_accept_less: true,
  outputs_added: true,
  outputs_removed: true,
  outputs_changed_kind: true,
  step_prompt_changed: true,
  step_tool_changed: true,
  step_method_changed: true,
  steps_added: true,
  steps_removed: true,
  steps_reordered: true,
  steps_replaced: true,
  step_versions_changed: true,
  wiring_changed: true,
  labels_added: true,
  labels_removed: true,
  label_terms_changed: true,
  labels_changed: true,
  tool_interface_changed: true,
  tool_code_changed: true,
  content_changed: true,
  items_added: true,
  items_removed: true,
  documents_added: true,
  documents_removed: true,
  documents_changed: true,
});
const WORKFLOW_EDITS = enumMembers<WorkflowEdit>({ update: true, derive: true });
const UPDATE_UNAVAILABLE = enumMembers<UpdateUnavailable>({ library: true });

export interface VersionLabel {
  /** `null`: the project content or a corpus as a whole. */
  readonly itemId: string | null;
  /** `major.minor.patch`. */
  readonly version: string;
  /** The exact version: `<item id>@<blob>` or `<collection id>@<commit>`. */
  readonly reference: string;
  readonly changeLine: string;
  /** `null` for a subject's first label, 1.0.0. */
  readonly bump: VersionBump | null;
  readonly reasons: readonly ChangeReason[];
  /** Whether the person merging confirmed the bump; `false` for DMW's proposal. */
  readonly confirmed: boolean;
  readonly labelledAt: string;
}

export interface LabelledReference {
  readonly reference: string;
  /** `null` where the version has no label. */
  readonly label: VersionLabel | null;
}

/** One subject a change set changes: "Draft based on 1.2.0 · proposes 1.3.0 · why". */
export interface SubjectPlan {
  /** `null`: the project content as a whole. */
  readonly itemId: string | null;
  /** `null` for a subject without a label yet, which starts at 1.0.0. */
  readonly current: string | null;
  readonly proposed: string | null;
  /** DMW's proposal; `null` for a new or removed item. */
  readonly bump: VersionBump | null;
  readonly reasons: readonly ChangeReason[];
  /** Proposed as minor, but major if it changes what the item does. */
  readonly mayChangeMethod: boolean;
  readonly removed: boolean;
  /** What each choice would give, from the server; empty for a new or removed item. */
  readonly versionsByBump: Readonly<Partial<Record<VersionBump, string>>>;
}

export interface VersionPlan {
  readonly changeSetId: string;
  /** The change set head the plan is for. */
  readonly head: string;
  readonly whole: SubjectPlan;
  readonly items: readonly SubjectPlan[];
}

/** F33: "Update <W> to 1.3.0" or "Save as a new Workflow derived from <W> 1.2.0". */
export interface WorkflowEditChoice {
  readonly workflow: string;
  readonly defaultEdit: WorkflowEdit;
  readonly current: string | null;
  readonly updated: string | null;
  /** Why updating isn't offered, or `null` when it is. */
  readonly updateUnavailable: UpdateUnavailable | null;
  readonly reasons: readonly ChangeReason[];
  readonly mayChangeMethod: boolean;
}

export function readNullableVersionBump(object: JsonObject, member: string): VersionBump | null {
  return object[member] == null ? null : readOneOf(object, member, VERSION_BUMPS);
}

/** `{item id: bump}`; a missing member is empty. */
export function readItemBumps(object: JsonObject, member: string): Readonly<Record<string, VersionBump>> {
  if (object[member] === undefined) return {};
  const bumps = readObject(object[member], `"${member}"`);
  return Object.fromEntries(Object.keys(bumps).map((itemId) => [itemId, readOneOf(bumps, itemId, VERSION_BUMPS)]));
}

function readReasons(object: JsonObject): ChangeReason[] {
  return readArray(object["reasons"], "change reasons", (reason) => readOneOf({ reason }, "reason", CHANGE_REASONS));
}

export function readVersionLabel(value: unknown): VersionLabel {
  const label = readObject(value, "version label");
  return {
    itemId: readNullableString(label, "item_id"),
    version: readString(label, "version"),
    reference: readString(label, "reference"),
    changeLine: readString(label, "change_line"),
    bump: readNullableVersionBump(label, "bump"),
    reasons: readReasons(label),
    confirmed: readBoolean(label, "confirmed"),
    labelledAt: readString(label, "labelled_at"),
  };
}

export function readLabelledReference(value: unknown): LabelledReference {
  const labelled = readObject(value, "labelled reference");
  return {
    reference: readString(labelled, "reference"),
    label: labelled["label"] === null ? null : readVersionLabel(labelled["label"]),
  };
}

function readVersionsByBump(plan: JsonObject): Partial<Record<VersionBump, string>> {
  const versions = readObject(plan["versions_by_bump"], '"versions_by_bump"');
  return Object.fromEntries(VERSION_BUMPS.filter((bump) => versions[bump] !== undefined).map((bump) => [bump, readString(versions, bump)]));
}

function readSubjectPlan(value: unknown): SubjectPlan {
  const plan = readObject(value, "subject plan");
  return {
    itemId: readNullableString(plan, "item_id"),
    current: readNullableString(plan, "current"),
    proposed: readNullableString(plan, "proposed"),
    bump: readNullableVersionBump(plan, "bump"),
    reasons: readReasons(plan),
    mayChangeMethod: readBoolean(plan, "may_change_method"),
    removed: readBoolean(plan, "removed"),
    versionsByBump: readVersionsByBump(plan),
  };
}

export function readVersionPlan(value: unknown): VersionPlan {
  const plan = readObject(value, "version plan");
  return {
    changeSetId: readString(plan, "change_set_id"),
    head: readString(plan, "head"),
    whole: readSubjectPlan(plan["whole"]),
    items: readArray(plan["items"], "subject plans", readSubjectPlan),
  };
}

export function readWorkflowEditChoice(value: unknown): WorkflowEditChoice {
  const choice = readObject(value, "workflow edit choice");
  return {
    workflow: readString(choice, "workflow"),
    defaultEdit: readOneOf(choice, "default", WORKFLOW_EDITS),
    current: readNullableString(choice, "current"),
    updated: readNullableString(choice, "updated"),
    updateUnavailable: choice["update_unavailable"] == null ? null : readOneOf(choice, "update_unavailable", UPDATE_UNAVAILABLE),
    reasons: readReasons(choice),
    mayChangeMethod: readBoolean(choice, "may_change_method"),
  };
}
